import moment from 'moment';
import type { DentalOfficeShiftCardStatus } from '@tempmee/react-blocks';
import type { TShiftCardData } from '~/types/ShiftCard';
import { getProfessionalName } from '~/utils/professional';
import { computeHours, formatRate } from '~/utils/shiftCard';

/**
 * Minimal shape of a "today's professional" job (redux `state.job.todayJobs`, raw API) —
 * only the fields the shift card consumes. The raw job nests times under `shift` and uses
 * the API spellings (`offer_owner`), so those are matched here rather than the `IJob` model.
 */
export interface ITodayJobShift {
  start?: string | Date;
  end?: string | Date;
  local_start_time?: string;
  local_end_time?: string;
}

export interface ITodayJobProfessional {
  _id?: string;
  full_name?: string;
  image?: string;
}

export interface ITodayJob {
  id?: string;
  _id?: string;
  rate?: number;
  profession?: string;
  subProfession?: string | null;
  isOnsiteInterview?: boolean;
  incident?: unknown;
  professional?: ITodayJobProfessional | null;
  offer_owner?: { _id?: string; office?: string } | null;
  shift?: ITodayJobShift | null;
}

/** A today shift is "current" (in progress) when now falls within its [start, end) window. */
export const isShiftInProgress = (shift?: ITodayJobShift | null): boolean => {
  if (!shift?.start || !shift?.end) {
    return false;
  }
  const now = moment();
  return now.isSameOrAfter(moment(shift.start)) && now.isBefore(moment(shift.end));
};

/**
 * Translates a raw today's-professional job into the props for the DS
 * `DentalOfficeShiftCard`. "Today's Professionals" omits the date (all shifts are today);
 * only the in-progress shift shows the "Current shift" status line, the rest hide it via
 * `statusLabel: null`. Interviews carry the INTERVIEW tag and no hourly rate.
 */
export const mapTodayJobToShiftCard = (job: ITodayJob): TShiftCardData => {
  const isInterview = Boolean(job.isOnsiteInterview);
  const isCurrent = !isInterview && isShiftInProgress(job.shift);
  const status: DentalOfficeShiftCardStatus = isCurrent ? 'current' : 'scheduled';

  const data: TShiftCardData = {
    status,
    // Only the in-progress shift surfaces a status line ("Current shift"); the rest hide it.
    statusLabel: isCurrent ? undefined : null,
    startTime: job.shift?.local_start_time ?? '',
    endTime: job.shift?.local_end_time,
    hours: computeHours(job.shift?.local_start_time, job.shift?.local_end_time),
    rate: isInterview ? undefined : formatRate(job.rate),
    // `profession` / `subProfession` are already short codes (e.g. "RDH", "EFDA").
    profession: job.subProfession ?? job.profession,
    office: job.offer_owner?.office ?? '',
    interview: isInterview,
  };

  const name = getProfessionalName(job.professional);
  const avatarUrl = job.professional?.image || undefined;
  if (name || avatarUrl) {
    data.person = {
      ...(name ? { name } : {}),
      ...(avatarUrl ? { avatarUrl } : {}),
    };
  }

  return data;
};
